import FxxlCore from '@fxxl/core';
import FxxlError from '@fxxl/error';
import FxxlLeasingCalculator from '@fxxl/leasing_calculator';
import ValueFormatter from '@fxxl/fxxl_value_formatter';
import AbortableFetcher from '@fxxl/abortable_fetcher';

export default class FxxlLeasingCalculatorSetup {
    constructor(price, extraFee = 0) {
        this.price = +price;
        this.extraFee = +extraFee;
        this.cookieName = FxxlCore.getConfig()?.cookieNames.leasingCalculatorData;
        this.leasingCalculator = new FxxlLeasingCalculator();
        this.form = null;
        this.headElement = null;
        this.gradiantElement = null;
        this.netRateElement = null;
        this.savingsElement = null;
        this.totalCosts = null;
        this.widgets = null;
        this.hiddenClass = 'fxxl-hidden';
        this.headIsStuckClass = 'fxxl-leasing-calculator__head--is-stuck';
        this.gradiantShowClass = 'fxxl-leasing-calculator__gradiant--show';
        this.headValueLoadingClasses = [
            'fxxl-loading-spinner-with-text',
            'fxxl-loading-spinner-with-text--large',
            'fxxl-loading-spinner-with-text--gray',
            'fxxl-leasing-calculator__head-item-value--loading'
        ];
        this.loadingAnimationDuration = 1000;
        this.isLoadingAnimationRunning = false;
        this.leasingFeeApiUrl = '/json.php?service=fxxlCalculateLeasingFee&price=###price###&uvp=###uvp###';

        // DAS HIER IST DEINE "SINGLE SOURCE OF TRUTH"
        // Du definierst hier:
        // 1. Wie heißt die Eigenschaft in der Klasse (prop)
        // 2. Wie finde ich sie im HTML (selector)
        // 3. Wie heißt der Key im Cookie/Daten-Objekt (dataKey)
        this.fields = [
            { prop: 'productPriceInput', selector: '[name="fxxl-leasing-bike-amount"]', dataKey: 'productPrice' },
            { prop: 'productUVPInput', selector: '[name="fxxl-leasing-bike-uvp"]', dataKey: 'productUVP' },
            { prop: 'salaryInput',       selector: '[name="fxxl-leasing-salary"]', dataKey: 'salary' },
            { prop: 'healthInput',       selector: '[name="fxxl-leasing-health-insurance"]', dataKey: 'healthInsurance' },
            { prop: 'churchTaxInput',    selector: '[name="fxxl-leasing-churchtax"]',       dataKey: 'churchTax' },
            { prop: 'taxInput',          selector: '[name="fxxl-leasing-taxclass"]',        dataKey: 'taxClass' },
            { prop: 'coveragePkgInput',  selector: '[name="fxxl-leasing-coverage-pkg"]',    dataKey: 'coveragePkg' }
        ];

        this.init();
    }

    init() {
        // Leasing Widgets initialisieren
        this.widgets = document.querySelectorAll('.fxxl-leasing-calculator-widget');
        this.setupLeasingWidgets();

        this.form = document.querySelector('.fxxl-leasing-calculator');

        if (!this.form || this.form.fxxl_leasing_calculator_init) {
            return;
        }

        const missingElements = [];

        this.headElement = document.querySelector('.fxxl-leasing-calculator__head');
        if (!this.headElement) {
            missingElements.push('header');
        }

        this.gradiantElement = document.querySelector('.fxxl-leasing-calculator__gradiant');
        if (!this.gradiantElement) {
            missingElements.push('gradiantElement');
        }

        this.netRateElement = document.querySelector('.fxxl-leasing-calculator__head-item-value--net-rate');
        if (!this.netRateElement) {
            missingElements.push('netRateElement');
        }

        this.savingsElement = document.querySelector('.fxxl-leasing-calculator__head-item-value--savings');
        if (!this.savingsElement) {
            missingElements.push('savingsElement');
        }

        this.totalCosts = document.querySelector('.fxxl-leasing-calculator__head-item-value--total-costs');
        if (!this.totalCosts) {
            missingElements.push('totalCosts');
        }

        // 1. Dynamisches Suchen aller Felder basierend auf der Config
        this.fields.forEach(field => {
            const el = this.form.querySelector(field.selector);
            if (el) {
                // Wir speichern das Element direkt in 'this', z.B. this.salaryInput
                this[field.prop] = el;
            } else {
                missingElements.push(field.prop);
            }
        });

        // Fehlercheck
        if (missingElements.length > 0) {
            new FxxlError(`Setup Error: Missing elements: ${missingElements.join(', ')}`);
            return;
        }

        if (this.isScrollable(this.form)) {
            this.gradiantElement.classList.add(this.gradiantShowClass);
        }

        // Alles da? Dann los.
        this.fillInputs();
        this.bindEvents();

        this.form.fxxl_leasing_calculator_init = true;
    }

    setupLeasingWidgets() {
        this.widgets.forEach(async widget => {
            const calculatedData = await this.calculate(+widget.dataset.price, +widget.dataset.leasingfee, true);
            const values = {
                monatliche_rate: calculatedData.netRate,
                ersparnis: calculatedData.savings,
                preis: calculatedData.totalCosts
            };

            if (!widget.dataset.dynamictext) {
                widget.setAttribute('data-dynamictext', widget.textContent);
            }

            const template = widget.dataset.dynamictext;

            const widgetNewText = template.replace(/###(monatliche_rate|preis|ersparnis)###/g, (match, key) => {
                return values[key] ?? match;
            });
            widget.textContent = widgetNewText;
            // Sichtbar machen
            widget.classList.remove(this.hiddenClass);
        });
    }

    isScrollable(element) {
        return element.scrollHeight > element.clientHeight;
    }

    bindEvents() {

        requestAnimationFrame(() => {
            if (!window?.isLargeViewport()) {
                this.form.addEventListener('scroll', (e) => {
                    this.headElement.classList.toggle(this.headIsStuckClass, this.form.scrollTop > 0);
                }, { passive: true });
            }
        });

        // Berechnen
        this.form.addEventListener('submit', (e) => {
            e.preventDefault();
            // Wenn alle Validierungen passen, dann berechnen
            if (this.form.fxxlValidation?.valid) {
                this.calculateWithLoadingAnimation();
            }
        });
    }

    async calculateWithLoadingAnimation() {
        if (this.isLoadingAnimationRunning) {
            return;
        }

        const headValueElements = [this.netRateElement, this.savingsElement, this.totalCosts];

        this.isLoadingAnimationRunning = true;
        headValueElements.forEach(element => element.classList.add(...this.headValueLoadingClasses));

        try {
            await new Promise(resolve => setTimeout(resolve, this.loadingAnimationDuration));
            await this.calculate();
        } finally {
            headValueElements.forEach(element => element.classList.remove(...this.headValueLoadingClasses));
            this.isLoadingAnimationRunning = false;
        }
    }

    // 3. Automatisch Daten einsammeln (kein manuelles Mapping mehr!)
    collectData() {
        const data = {};

        this.fields.forEach(field => {
            const el = this[field.prop];
            // Automatische Unterscheidung Checkbox vs Value
            let val;
            if (el.type === 'checkbox') {
                val = el.checked;
            } else if (el.type === 'radio') {
                const checkedRadio = this.form.querySelector(`${field.selector}:checked`);
                val = checkedRadio ? checkedRadio.value : null;
            } else {
                val = el.value;
            }

            // Speichere unter dem sauberen Namen (z.B. 'salary')
            data[field.dataKey] = val;
        });

        return data;
    }

    validateData(data) {
        // Beispielvalidierung: Alle Felder müssen ausgefüllt sein
        for (const key in data) {
            if (data[key] === null || data[key] === undefined || data[key] === '') {
                return false;
            }
        }
        return true;
    }

    // 4. Automatisch Daten zurückschreiben (beim Laden)
    fillInputs() {
        const data = this.data;

        if (!data) {
            return;
        }

        this.fields.forEach(field => {
            const el = this[field.prop];
            const savedValue = data[field.dataKey];
            if (savedValue !== undefined) {
                if (el.type === 'checkbox') {
                    el.checked = !!savedValue;
                } else if (el.type === 'radio') {
                    const radioToCheck = this.form.querySelector(`${field.selector}[value="${savedValue}"]`);
                    if (radioToCheck) {
                        radioToCheck.checked = true;
                    }
                } else {
                    if (field.dataKey === 'productPrice' && this.price) {
                        el.value = ValueFormatter.format(this.price, 'currency');
                    } else {
                        el.value = savedValue;
                    }
                    FxxlCore.eventEmitter('change').emit(null, el);
                }
            }
        });
        // Nach dem Befüllen direkt berechnen
        if (this.validateData(data)) {
            this.calculate();
        }
    }

    getDataFromCookie() {
        let data = this.data;
        if (!data) {
          data = {};
        }
        return {
            productPrice: data.productPrice || 4000,
            productUVP: data.productUVP || 0,
            salary: data.salary || 3000,
            healthInsurance: data.healthInsurance || 'statutory',
            churchTax: data.churchTax || 1,
            taxClass: data.taxClass || 3,
            coveragePkg: data.coveragePkg || 'employer'
        };
    }

    async calculate(price = 0, extraFee = 0, getFromCookie = false) {
        let data;
        let result = {};
        let leasingFee = extraFee || this.extraFee;

        if (!getFromCookie) {
            data = this.collectData();
        } else {
            data = this.getDataFromCookie();
        }

        if (price > 0) {
            data.productPrice = ValueFormatter.format(price, 'currency');
        }

        const uvp =ValueFormatter.format(data.productUVP, 'number');
        if (leasingFee === 0 && uvp > 0) {
            if (window?.showSpinner) {
                window.showSpinner();
            }
            const leasingFeeData = await new AbortableFetcher(
                this.leasingFeeApiUrl.replace('###price###', ValueFormatter.format(data.productPrice, 'number'))
                    .replace('###uvp###', uvp)
            ).fetchJson();
            if (window?.hideSpinner) {
                window.hideSpinner();
            }
            leasingFee = leasingFeeData?.leasing_fee || 0;
        }

        this.data = data;

        if (data) {
            const totalPrice = parseFloat(+ValueFormatter.format(data.productPrice, 'number') + leasingFee);
            const calculatedData = this.leasingCalculator.calculateLeasingCost(
                {
                    productPrice: totalPrice,
                    provider: 'jobrad',
                    grossIncome: ValueFormatter.format(data.salary, 'number'),
                    federalState: 'HE',
                    taxClass: data.taxClass,
                    childrenCount: 0,
                    churchTax: +data.churchTax === 1,
                    healthInsuranceType: data.healthInsurance,
                    healthInsuranceAdditionalContribution: 2.5,
                    birthYear: 1979,
                    childless: true,
                    employerPaysProtectionPackage: data.coveragePkg === 'employer'
                }
            );

            const privateBuyCosts = Math.round((parseInt(ValueFormatter.format(data.productPrice, 'number')) + calculatedData.protectionPackageCost));

            const leaseBuyCosts = Math.round(
                calculatedData.netCompleteCost +
                calculatedData.protectionPackageCost +
                calculatedData.service_fee +
                leasingFee
            );

            const savings = Math.max(privateBuyCosts - leaseBuyCosts, 0);
            const formattedNetRate = `${ValueFormatter.format(Math.ceil(calculatedData.netMonthlyCost), 'currency_eur')}`;
            const formattedSavings = `${ValueFormatter.format(savings, 'currency_eur')}`;
            const formattedTotalCost = `${ValueFormatter.format(calculatedData.netCompleteCost, 'currency_eur')}`;
            if (this.netRateElement && this.savingsElement && this.totalCosts) {
                // Update: Text Elements
                this.netRateElement.textContent = formattedNetRate;
                this.savingsElement.textContent = formattedSavings;
                this.totalCosts.textContent = formattedTotalCost;

                // Send: DataLayer Push
                dataLayer.push({
                    event: 'leasingrechner_submit',
                    leasingrechner: {
                        // Nutzereingaben
                        fahrradpreis:                   parseInt(ValueFormatter.format(data.productPrice, 'number')),
                        uvppreis:                       parseInt(ValueFormatter.format(data.productUVP, 'number')),
                        schutzpaket_kostenuebernahme:   data.coveragePkg,

                        // Ausgabe / Ergebnisse des Rechners
                        berechnete_nettobelastung:      calculatedData.netMonthlyCost,
                        berechnete_ersparnis:           savings,
                        berechnete_gesamtkosten:        calculatedData.netCompleteCost
                    }
                });

            }

            result = {
                netRate: formattedNetRate,
                savings: formattedSavings,
                totalCosts: formattedTotalCost,
            };
        }

        return result;
    }

    get data() {
        return FxxlCore.getCookie(this.cookieName);
    }

    set data(value) {
        FxxlCore.setCookie(this.cookieName, value, Date.now() + (1000 * 60 * 60 * 24 * 1)); // 1 Tag
    }
}